import { BusinessObjectBase } from './base';
import { processFunctionSchema } from '@schemas/business-objects/process-function';
import type { ProcessDataType } from './process-data-type';
import type { ProcessFunctionParameter } from './process-function-parameter';
import type { Id } from '../base-types';

export interface ProcessFunctionData {
  readonly id: Id;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly returnsArray?: boolean;
  readonly typescript?: string;
  readonly parameters?: ProcessFunctionParameter[];
  readonly returnTypeId: Id;
  readonly returnType?: ProcessDataType;
}

export interface ProcessFunction extends Readonly<ProcessFunctionData> {}
type ProcessFunctionCreateWithChildrenData = Partial<
  Omit<ProcessFunctionData, 'parameters'>
> & { parameters?: Partial<ProcessFunctionParameter>[] };

export class ProcessFunction extends BusinessObjectBase {
  static readonly entityName = 'ProcessFunction' as const;
  static readonly plural = 'process-functions';
  static schema = processFunctionSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessFunction[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessFunction(data));
  }

  static async findOne(filter?: any): Promise<ProcessFunction | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessFunction(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessFunction> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessFunction(data);
  }

  static async create(
    data: Omit<ProcessFunctionData, 'id'>,
  ): Promise<ProcessFunction> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessFunction(result);
  }

  static async createMany(
    data: Omit<ProcessFunctionData, 'id'>[],
  ): Promise<ProcessFunction[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessFunction(item));
  }

  static async createWithChildren(
    data: ProcessFunctionCreateWithChildrenData,
  ): Promise<ProcessFunction> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessFunction(result);
  }

  static async createManyWithChildren(
    data: ProcessFunctionCreateWithChildrenData[],
  ): Promise<ProcessFunction[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessFunction(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessFunctionData>,
  ): Promise<ProcessFunction> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessFunction(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
